// src/components/ConfirmDialog.jsx
// window.confirm / window.prompt の代わりの、画面の中に出す確認ダイアログ【2026-11-02】
//
// ブラウザの確認窓は見た目が選べず、何が消えるのかを強調できない。スマホでは文が途中で切れることもある。
//   if (!(await confirmDialog('「A」を削除します。\n\n元には戻せません。'))) return;
//   const v = await promptDialog('件数を入力してください', '');   // キャンセルは null
// 文の 1 行目（改行の前）が短ければ、見出しとして太字で出す。
// 「削除」「消える」「元に戻せない」などが入っていれば、OK ボタンを赤にして、最初はキャンセルに合わせておく。
// opts: { title, okLabel, cancelLabel, danger }（指定すれば上の自動判定より優先）
// ダイアログを出す場所（<DialogHost />）は Layout.jsx に置いてある。置いていない画面（ログインなど）ではブラウザの確認窓を使う。

import React, { useEffect, useRef, useState } from 'react';

let host = null;   // DialogHost が登録する

const DANGER_RE = /削除|消し|消え|消す|消去|取り消|無効化|元には戻せ|元に戻せ|戻せません|なかったこと|統合|外します|退出|ブロック/;

function normalize(message, opts = {}, kind) {
  const text = String(message == null ? '' : message);
  let title = opts.title || '';
  let body = text;
  if (!title) {
    const i = text.indexOf('\n');
    if (i > 0 && i <= 60) { title = text.slice(0, i); body = text.slice(i + 1).replace(/^\n+/, ''); }
    else if (i < 0 && text.length <= 40) { title = text; body = ''; }
  }
  const danger = opts.danger != null ? !!opts.danger : DANGER_RE.test(text);
  return {
    kind, title, body, danger,
    okLabel: opts.okLabel || (danger ? '実行する' : 'OK'),
    cancelLabel: opts.cancelLabel || 'キャンセル',
    defaultValue: opts.defaultValue != null ? String(opts.defaultValue) : '',
  };
}

/** 確認。OK なら true */
export function confirmDialog(message, opts) {
  if (!host) return Promise.resolve(window.confirm(message));
  return new Promise(resolve => host.open(normalize(message, opts, 'confirm'), resolve));
}

/** 入力。キャンセルなら null（window.prompt と同じ） */
export function promptDialog(message, defaultValue = '', opts = {}) {
  if (!host) return Promise.resolve(window.prompt(message, defaultValue));
  return new Promise(resolve => host.open(normalize(message, { ...opts, defaultValue }, 'prompt'), resolve));
}

export function DialogHost() {
  const [queue, setQueue] = useState([]);   // 同時に来たら順に出す
  useEffect(() => {
    const me = { open: (d, resolve) => setQueue(q => [...q, { ...d, resolve, id: Math.random() }]) };
    host = me;
    return () => { if (host === me) host = null; };
  }, []);
  const cur = queue[0];
  if (!cur) return null;
  const done = (v) => { cur.resolve(v); setQueue(q => q.slice(1)); };
  return <Dialog key={cur.id} d={cur} onDone={done} />;
}

function Dialog({ d, onDone }) {
  const [value, setValue] = useState(d.defaultValue);
  const boxRef = useRef(null);
  const okRef = useRef(null);
  const cancelRef = useRef(null);
  const inputRef = useRef(null);
  const before = useRef(typeof document !== 'undefined' ? document.activeElement : null);

  const cancel = () => onDone(d.kind === 'prompt' ? null : false);
  const ok = () => onDone(d.kind === 'prompt' ? value : true);

  useEffect(() => {
    // 危ない操作は、うっかり Enter で進まないようにキャンセルに合わせておく
    const t = setTimeout(() => {
      if (d.kind === 'prompt' && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
      else if (d.danger && cancelRef.current) cancelRef.current.focus();
      else if (okRef.current) okRef.current.focus();
    }, 0);
    const prev = before.current;
    return () => { clearTimeout(t); if (prev && prev.focus) { try { prev.focus(); } catch (e) { /* 消えていれば何もしない */ } } };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- 出たときに 1 回だけ

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); cancel(); return; }
    if (e.key === 'Tab' && boxRef.current) {
      // ダイアログの外へフォーカスが出ないように
      const els = boxRef.current.querySelectorAll('button, input');
      if (!els.length) return;
      const first = els[0], last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  };

  return (
    <div className="cdlg-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) cancel(); }}>
      <div className={'cdlg' + (d.danger ? ' cdlg-danger' : '')} role="alertdialog" aria-modal="true"
           aria-labelledby="cdlg-title" aria-describedby="cdlg-body" ref={boxRef} onKeyDown={onKeyDown}>
        {d.danger && <div className="cdlg-badge" aria-hidden="true">!</div>}
        {d.title && <h2 id="cdlg-title" className="cdlg-title">{d.title}</h2>}
        {d.body && <div id="cdlg-body" className="cdlg-body">{highlight(d.body)}</div>}
        {d.kind === 'prompt' && (
          <input ref={inputRef} className="cdlg-input" value={value}
                 onChange={e => setValue(e.target.value)}
                 onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); ok(); } }} />
        )}
        <div className="cdlg-actions">
          <button type="button" ref={cancelRef} className="btn-secondary" onClick={cancel}>{d.cancelLabel}</button>
          <button type="button" ref={okRef} className={d.danger ? 'btn-danger' : 'btn-primary'} onClick={ok}>{d.okLabel}</button>
        </div>
      </div>
    </div>
  );
}

// 「元には戻せません」など、取り返しのつかないことを書いた行は赤く太字にする
function highlight(body) {
  return body.split('\n').map((line, i) => (
    <React.Fragment key={i}>
      {i > 0 && <br />}
      {/元には戻せ|元に戻せ|戻せません|一緒に消え|消えます|削除され/.test(line)
        ? <strong className="cdlg-warn">{line}</strong>
        : line}
    </React.Fragment>
  ));
}
